import { useServer } from 'app/server/context'
import * as Application from 'expo-application'
import { isDevice, modelName, osName, osVersion } from 'expo-device'
import * as Updates from 'expo-updates'
import React, { createContext, useContext } from 'react'
import { Platform } from 'react-native'
import { ServerAnalyticsService } from './index'
import { AnalyticsService, BaseAnalyticsEntity } from './types'

export const AnalyticsContext = createContext({} as AnalyticsService)

interface Props {
  children: React.ReactNode
}

export function AnalyticsProvider(props: Props) {
  const { server } = useServer()

  const version = Application?.nativeApplicationVersion
  const updateId = Updates.updateId

  const analyticsEntity: BaseAnalyticsEntity = {
    platform: Platform.OS.toUpperCase(),
    device: isDevice && modelName ? modelName : 'Simulator',
    os: osName && osVersion ? `${osName} ${osVersion}` : null,
    browser: getBrowserInfo(window.navigator.userAgent),
    client_version: version,
    update_uuid: updateId ?? 'DEV_UUID',
  }

  if (!server) {
    return null
  }

  return (
    <AnalyticsContext.Provider
      value={new ServerAnalyticsService(server, analyticsEntity)}
    >
      {props.children}
    </AnalyticsContext.Provider>
  )
}

export function useAnalytics() {
  return useContext<AnalyticsService>(AnalyticsContext)
}

function getBrowserInfo(userAgent: string): string | null {
  // The order matters here, and this may report false positives for unlisted browsers.

  let index: number = -1
  let name: string = ''
  let version: string = ''

  if (!userAgent) {
    return null
  }

  if (userAgent.includes('Firefox')) {
    // "Mozilla/5.0 (X11; Linux i686; rv:104.0) Gecko/20100101 Firefox/104.0"
    index = userAgent.indexOf('Firefox')
    name = 'Mozilla Firefox'
  } else if (userAgent.includes('SamsungBrowser')) {
    // "Mozilla/5.0 (Linux; Android 9; SAMSUNG SM-G955F Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/9.4 Chrome/67.0.3396.87 Mobile Safari/537.36"
    index = userAgent.indexOf('SamsungBrowser')
    name = 'Samsung Internet'
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    // "Mozilla/5.0 (Macintosh; Intel Mac OS X 12_5_1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36 OPR/90.0.4480.54"
    index = userAgent.indexOf('Opera')
    if (index < 1) {
      index = userAgent.indexOf('OPR')
    }
    name = 'Opera'
  } else if (userAgent.includes('Edge')) {
    // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
    index = userAgent.indexOf('Edge')
    name = 'Microsoft Edge (Legacy)'
  } else if (userAgent.includes('Edg')) {
    // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36 Edg/104.0.1293.70"
    index = userAgent.indexOf('Edg')
    name = 'Microsoft Edge (Chromium)'
  } else if (userAgent.includes('Chrome')) {
    // "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36"
    index = userAgent.indexOf('Chrome')
    name = 'Google Chrome or Chromium'
  } else if (userAgent.includes('Safari')) {
    // "Mozilla/5.0 (iPhone; CPU iPhone OS 15_6_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.6 Mobile/15E148 Safari/604.1"
    index = userAgent.indexOf('Safari')
    name = 'Apple Safari'
  } else {
    name = 'Unknown'
  }

  if (index > -1) {
    version = userAgent.substring(index).split('/')[1].trim()
  }

  return `${name} ${version ?? ''}`
}
